import type {
  ScorecardCurrentSnapshotOut,
  ScorecardMetricOut,
  ScorecardResponse,
  ScorecardSectionOut,
  ScorecardSnapshotNotesOut,
  ScorecardSnapshotSummaryOut,
  ScorecardWorkstreamContextOut,
} from 'client';
import {WorkstreamMetricVisibility} from 'client';
import type {SparklineTrend} from 'components/DSYS/Sparkline';
import {parseMetricValueString} from 'helpers/compactMetricValue';

export type ScorecardTabId = string;
export type ScorecardTrend = SparklineTrend;

export type ScorecardResponseWithSnapshotNotes = ScorecardResponse;

export type ScorecardMetric = {
  id: number;
  sectionId: ScorecardTabId;
  sectionLabel?: string;
  name: string;
  description?: string;
  isMnpi: boolean;
  visibility: WorkstreamMetricVisibility;
  currentUpdatedAt?: string;
  currentUpdatedAts: (string | null)[];
  sourceWorkstreamId?: number;
  sourceWorkstreamName?: string;
  sourceWorkstreamContext?: ScorecardWorkstreamContextOut;
  groupId?: string;
  groupLabel?: string;
  isPillarNorthStar: boolean;
  workstreamIds: number[];
  values: (string | null)[];
  quarterActuals: (string | null)[];
  currentQuarterBudgets: (string | null)[];
  currentQuarterTargets: (string | null)[];
  nextQuarterBudgets: (string | null)[];
  nextQuarterTargets: (string | null)[];
  eoyBudgets: (string | null)[];
  desiredDirection?: ScorecardMetricOut['desired_direction'];
  snapshotDts: (string | null)[];
  snapshotIds: (number | null)[];
  periodNotes: (string | undefined)[];
  statuses: (string | null)[];
  status?: string;
  target: string | null;
  baseline: string | null;
  higherIsBetter: boolean;
};

export type ScorecardSection = {
  id: ScorecardTabId;
  label: string;
  grouped?: boolean;
  metrics: ScorecardMetric[];
};

export const formatSnapshotLabel = (date: Date): string =>
  date.toLocaleString('en-US', {month: 'short', day: 'numeric', timeZone: 'UTC'});

export const formatQuarterLabel = (date: Date | null): string => {
  if (!date) {
    return 'Q--';
  }
  return `Q${Math.floor(date.getUTCMonth() / 3) + 1}`;
};

/** Stepper label for an end-of-quarter snapshot, e.g. "End of Q3". */
export const formatEndOfQuarterLabel = (date: Date): string => `End of ${formatQuarterLabel(date)}`;

/** Quarter Close option label for an end-of-quarter snapshot, e.g. "Q3 2026". */
export const formatQuarterCloseLabel = (date: Date): string =>
  `${formatQuarterLabel(date)} ${date.getUTCFullYear()}`;

export function parseApiDate(iso: string | null | undefined): Date | null {
  if (!iso) {
    return null;
  }
  const parts = iso.split('-').map(Number);
  if (parts.length < 3 || parts.some((n) => Number.isNaN(n))) {
    return null;
  }
  const [y, m, d] = parts;
  return new Date(Date.UTC(y, m - 1, d));
}

const normalizeNoteValue = (note: string | null | undefined): string | undefined =>
  note?.trim() || undefined;

export function mapApiMetric(metric: ScorecardMetricOut): ScorecardMetric {
  if (metric.is_mnpi == null || metric.visibility == null) {
    throw new Error('Scorecard metric response is missing required MNPI metadata');
  }

  const snapshots = metric.snapshots ?? [];
  return {
    id: metric.metric_id,
    sectionId: metric.section_id,
    sectionLabel: metric.section_label?.trim() || undefined,
    name: metric.display_name,
    description: metric.description?.trim() || undefined,
    isMnpi: metric.is_mnpi,
    visibility: metric.visibility,
    currentUpdatedAt: metric.current_updated_at ?? undefined,
    currentUpdatedAts: snapshots.map((snapshot) => snapshot?.current_updated_at ?? null),
    sourceWorkstreamContext: metric.source_workstream_context ?? undefined,
    sourceWorkstreamId: metric.source_workstream_id ?? undefined,
    sourceWorkstreamName: metric.source_workstream_name?.trim() || undefined,
    groupId: metric.group_id ?? undefined,
    groupLabel: metric.group_label?.trim() || undefined,
    isPillarNorthStar: metric.is_pillar_north_star ?? false,
    workstreamIds: metric.workstream_ids ?? [],
    values: snapshots.map((snapshot) => snapshot?.current ?? null),
    quarterActuals: snapshots.map((snapshot) => snapshot?.quarter_actual ?? null),
    currentQuarterBudgets: snapshots.map((snapshot) => snapshot?.current_quarter_budget ?? null),
    currentQuarterTargets: snapshots.map((snapshot) => snapshot?.current_quarter_target ?? null),
    nextQuarterBudgets: snapshots.map((snapshot) => snapshot?.next_quarter_budget ?? null),
    nextQuarterTargets: snapshots.map((snapshot) => snapshot?.next_quarter_target ?? null),
    eoyBudgets: snapshots.map((snapshot) => snapshot?.eoy_budget ?? null),
    desiredDirection: metric.desired_direction ?? undefined,
    snapshotDts: snapshots.map((snapshot) => snapshot?.snapshot_dt ?? null),
    snapshotIds: snapshots.map((snapshot) => snapshot?.id ?? null),
    periodNotes: snapshots.map((snapshot) => normalizeNoteValue(snapshot?.notes)),
    statuses: snapshots.map((snapshot) => snapshot?.status ?? null),
    status: snapshots[0]?.status ?? 'no_status',
    target: metric.target ?? null,
    baseline: metric.baseline ?? null,
    higherIsBetter: true,
  };
}

const slugifyScorecardTabLabel = (label: string): string =>
  label
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

export const buildScorecardTabParam = (section: Pick<ScorecardSection, 'id' | 'label'>): string => {
  if (!/^\d+$/.test(section.id)) {
    return section.id;
  }
  const slug = slugifyScorecardTabLabel(section.label);
  return slug ? `${section.id}-${slug}` : section.id;
};

export const parseScorecardTabParam = (value: string | null | undefined): ScorecardTabId | null => {
  if (!value) {
    return null;
  }
  const match = value.match(/^(\d+)(?:-|$)/);
  return match?.[1] ?? value;
};

export const applyScorecardSnapshotNotesUpdate = (
  oldScorecard: ScorecardResponseWithSnapshotNotes | undefined,
  response: ScorecardSnapshotNotesOut
): ScorecardResponseWithSnapshotNotes | undefined => {
  if (!oldScorecard?.metrics?.length) {
    return oldScorecard;
  }

  let didUpdateMetric = false;
  const metrics = oldScorecard.metrics.map((metric) => {
    let didUpdateThisMetric = false;
    const snapshots = metric.snapshots ?? [];
    const nextSnapshots = snapshots.map((snapshot) => {
      if (!snapshot || snapshot.id !== response.id) {
        return snapshot;
      }
      didUpdateMetric = true;
      didUpdateThisMetric = true;
      return {...snapshot, notes: response.notes ?? null};
    });
    return didUpdateThisMetric ? {...metric, snapshots: nextSnapshots} : metric;
  });

  return didUpdateMetric ? {...oldScorecard, metrics} : oldScorecard;
};

export const applyScorecardCurrentSnapshotEnsure = (
  oldScorecard: ScorecardResponseWithSnapshotNotes | undefined,
  response: ScorecardCurrentSnapshotOut
): ScorecardResponseWithSnapshotNotes | undefined => {
  if (!oldScorecard?.metrics?.length) {
    return oldScorecard;
  }

  const snapshotDt = response.snapshot_dt ?? 'current';
  let didUpdateMetric = false;
  const metrics = oldScorecard.metrics.map((metric) => {
    if (metric.metric_id !== response.metric_id) {
      return metric;
    }

    let didUpdateThisMetric = false;
    const snapshots = metric.snapshots ?? [];
    const nextSnapshots = snapshots.map((snapshot) => {
      if (!snapshot || snapshot.snapshot_dt !== snapshotDt) {
        return snapshot;
      }

      didUpdateMetric = true;
      didUpdateThisMetric = true;
      return {
        ...snapshot,
        id: response.id,
        notes: response.notes ?? snapshot.notes ?? null,
      };
    });
    return didUpdateThisMetric ? {...metric, snapshots: nextSnapshots} : metric;
  });

  return didUpdateMetric ? {...oldScorecard, metrics} : oldScorecard;
};

export const applyScorecardSnapshotSummaryUpdate = (
  oldScorecard: ScorecardResponseWithSnapshotNotes | undefined,
  response: ScorecardSnapshotSummaryOut
): ScorecardResponseWithSnapshotNotes | undefined => {
  if (!oldScorecard || oldScorecard.snapshot_dt !== response.snapshot_dt) {
    return oldScorecard;
  }

  return {
    ...oldScorecard,
    snapshot_summaries: {
      ...(oldScorecard.snapshot_summaries ?? {}),
      [response.tab_id]: response.summary,
    },
  };
};

export function getSnapshotPeriodsFromApi(periods: string[] | undefined): Date[] {
  return (periods ?? []).map((iso) => parseApiDate(iso)).filter((d): d is Date => Boolean(d));
}

export function buildScorecardSectionsFromApi(
  metrics: ScorecardMetricOut[] | undefined,
  sections: ScorecardSectionOut[] | undefined
): ScorecardSection[] {
  const sectionToMetrics = new Map<ScorecardTabId, ScorecardMetric[]>();
  const labels = new Map<ScorecardTabId, string>();
  const grouped = new Map<ScorecardTabId, boolean>();

  for (const section of sections ?? []) {
    sectionToMetrics.set(section.id, []);
    labels.set(section.id, section.label);
    grouped.set(section.id, section.grouped ?? false);
  }

  for (const metric of metrics ?? []) {
    const mappedMetric = mapApiMetric(metric);
    const sectionId = metric.section_id;
    const bucket = sectionToMetrics.get(sectionId);
    if (bucket) {
      bucket.push(mappedMetric);
    } else {
      sectionToMetrics.set(sectionId, [mappedMetric]);
      labels.set(sectionId, metric.section_label ?? sectionId);
      grouped.set(sectionId, false);
    }
  }

  return Array.from(sectionToMetrics.entries()).map(([id, sectionMetrics]) => ({
    id,
    label: labels.get(id) ?? id,
    grouped: grouped.get(id) ?? false,
    metrics: [...sectionMetrics],
  }));
}

export const getScorecardSections = (
  activeTab: ScorecardTabId | null,
  sections: ScorecardSection[]
): ScorecardSection[] => {
  if (activeTab) {
    const section = sections.find((s) => s.id === activeTab);
    return [section ?? {id: activeTab, label: activeTab, metrics: []}];
  }

  return sections.filter((section) => section.metrics.length > 0);
};

export const getTotalMetricCount = (sections: ScorecardSection[]): number =>
  sections.reduce((sum, s) => sum + s.metrics.length, 0);

export const getValueTrend = (
  currentValue: number | null,
  priorValue: number | null,
  higherIsBetter = true
): ScorecardTrend => {
  if (currentValue == null || priorValue == null || currentValue === priorValue) {
    return 'flat';
  }
  const increased = currentValue > priorValue;
  const isBetter = higherIsBetter ? increased : !increased;
  return isBetter ? 'up' : 'down';
};

export type MetricStatusTone = 'positive' | 'negative' | 'neutral';

export type MetricStatus = {
  label: string;
  tone: MetricStatusTone;
};

const TREND_WORD: Record<ScorecardTrend, string> = {
  up: 'Improving',
  down: 'Needs attention',
  flat: 'Flat',
};

const TREND_TONE: Record<ScorecardTrend, MetricStatusTone> = {
  up: 'positive',
  down: 'negative',
  flat: 'neutral',
};

export const getTrendBackgroundColor = (
  trend: ScorecardTrend
): 'Success Surface' | 'Alert Surface' | undefined => {
  if (trend === 'up') {
    return 'Success Surface';
  }
  if (trend === 'down') {
    return 'Alert Surface';
  }
  return undefined;
};

export const getMetricStatusFromValues = ({
  currentValue,
  targetValue,
  trend,
  higherIsBetter = true,
}: {
  currentValue: number | null;
  targetValue: number | null;
  trend: ScorecardTrend;
  higherIsBetter?: boolean;
}): MetricStatus => {
  const trendWord = TREND_WORD[trend];
  const tone = TREND_TONE[trend];

  if (currentValue == null || targetValue == null) {
    return {label: trendWord, tone};
  }

  const tolerance = Math.max(Math.abs(targetValue) * 0.005, Number.EPSILON);
  let targetLabel: string;
  if (Math.abs(currentValue - targetValue) <= tolerance) {
    targetLabel = 'On target';
  } else {
    const isBetter = higherIsBetter ? currentValue > targetValue : currentValue < targetValue;
    targetLabel = isBetter ? 'Above target' : 'Below target';
  }

  return {label: `${trendWord} • ${targetLabel}`, tone};
};

export const getMetricStatus = (
  metric: ScorecardMetric,
  currentValue: string | null,
  trend: ScorecardTrend
): MetricStatus =>
  getMetricStatusFromValues({
    currentValue: parseMetricValueString(currentValue),
    targetValue: parseMetricValueString(metric.target),
    trend,
    higherIsBetter: metric.higherIsBetter,
  });
